import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import { useLocation, matchPath } from 'react-router-dom';
import { discussionsApi, Discussion } from '../../api/ppm';
import { usersApi, DbUser } from '../../api/client';

function usernameFromEmail(email?: string | null) {
  const e = String(email || '').trim();
  if (!e.includes('@')) return e;
  return e.split('@')[0];
}

function getTarget(pathname: string) {
  const p = pathname || '/';
  const projectInternal = matchPath('/projects/internal/:id', p);
  if (projectInternal?.params?.id) return { entityType: 'project', entityId: String(projectInternal.params.id), title: 'Discussion' };

  const portfolio = matchPath('/portfolios/:id', p);
  if (portfolio?.params?.id) return { entityType: 'portfolio', entityId: String(portfolio.params.id), title: 'Discussion' };

  const jiraProject = matchPath('/projects/:projectKey', p);
  if (jiraProject?.params?.projectKey) return { entityType: 'jira-project', entityId: String(jiraProject.params.projectKey), title: 'Discussion' };

  return { entityType: 'page', entityId: p, title: 'Discussion' };
}

function prettifyPathname(pathname: string) {
  const p = String(pathname || '/').trim() || '/';
  if (p === '/' || p === '/dashboard') return 'Workspace';
  if (p.startsWith('/overview')) return 'Overview';
  if (p.startsWith('/okrs')) return 'OKRs';
  if (p.startsWith('/portfolios')) return 'Portfolios';
  if (p.startsWith('/projects')) return 'Projects';
  if (p.startsWith('/requests')) return 'Requests';
  if (p.startsWith('/financials')) return 'Financials';
  if (p.startsWith('/capacity')) return 'Capacity';
  if (p.startsWith('/roadmaps')) return 'Roadmaps';
  if (p.startsWith('/raid')) return 'RAID';
  if (p.startsWith('/templates')) return 'Templates';
  if (p.startsWith('/timesheets')) return 'Timesheets';
  if (p.startsWith('/settings') || p.startsWith('/user-settings')) return 'Settings';
  if (p.startsWith('/team')) return 'Team';

  const clean = p.split('?')[0].split('#')[0];
  const last = clean.split('/').filter(Boolean).slice(-1)[0] || clean;
  const words = last.replace(/[-_]/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Page';
}

function prettifyDiscussionContext(target: { entityType: string; entityId: string }, pathname: string) {
  if (target.entityType === 'page') return prettifyPathname(pathname);
  if (target.entityType === 'portfolio') return `Portfolio • ${target.entityId}`;
  if (target.entityType === 'project') return `Internal Project • ${target.entityId}`;
  if (target.entityType === 'jira-project') return `Jira Project • ${target.entityId}`;
  return `${target.entityType} • ${target.entityId}`;
}

export const InlineDiscussion: React.FC = () => {
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [discussion, setDiscussion] = useState<Discussion | null>(null);
  const [meUser, setMeUser] = useState<DbUser | null>(null);
  const [message, setMessage] = useState('');

  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const target = useMemo(() => getTarget(location.pathname), [location.pathname]);

  const me = meUser;

  const myUserId = me?.id || '';

  const shouldShow = useMemo(() => {
    const p = location.pathname || '/';
    // Strip optional tenant-slug prefix (e.g. "/cetana/settings" → "/settings")
    const stripped = p.replace(/^\/[^/]+/, '');
    if (stripped.startsWith('/team')) return false;
    if (stripped.startsWith('/settings') || stripped === '/settings') return false;
    if (stripped.startsWith('/user-settings') || stripped === '/user-settings') return false;
    if (stripped.startsWith('/dashboard')) return false; // Workspace
    if (stripped.startsWith('/management')) return false;
    if (p.startsWith('/auth/')) return false;
    if (p === '/') return false;
    return true;
  }, [location.pathname]);

  const load = async (opts?: { autoScroll?: boolean }) => {
    setErr(null);
    setLoading(true);
    try {
      // Never scroll the page on load. If we do scroll, do it inside the discussion scroller only.
      const el = scrollerRef.current;
      const isNearBottom = !!el && el.scrollHeight - el.scrollTop - el.clientHeight < 28;
      const shouldScrollToBottom = !!opts?.autoScroll || isNearBottom;

      // Ensure "me" exists (works for both admin and non-admin; no need to list all users)
      const ensuredUser = await usersApi.me().catch(async () => {
        await usersApi.ensure();
        return usersApi.me();
      });
      setMeUser((ensuredUser.data || null) as DbUser | null);

      const ensured = await discussionsApi.ensure({
        entityType: target.entityType,
        entityId: target.entityId,
        title: target.title,
      });
      const d = await discussionsApi.getOne(ensured.data.id);
      setDiscussion(d.data || null);

      if (shouldScrollToBottom) {
        requestAnimationFrame(() => {
          const s = scrollerRef.current;
          if (!s) return;
          s.scrollTop = s.scrollHeight;
        });
      }
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load discussion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldShow) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, target.entityType, target.entityId]);

  const send = async () => {
    const content = message.trim();
    if (!content || !discussion?.id) return;
    if (!myUserId) {
      setErr('Unable to resolve your DB user yet. Refresh the page and try again.');
      return;
    }
    setErr(null);
    try {
      await discussionsApi.addPost(discussion.id, { content });
      setMessage('');
      await load({ autoScroll: true });
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to send message');
    }
  };

  if (!shouldShow) return null;

  const posts = discussion?.Posts || [];
  const contextLabel = prettifyDiscussionContext(target as any, location.pathname);

  return (
    <Container maxWidth="xl" sx={{ mt: 0, pb: 0 }}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: 'rgba(0,0,0,0.08)',
          background: '#ffffff',
          boxShadow: 'none',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Discussion
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {contextLabel}
            </Typography>
          </Box>

          {err ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
              {err}
            </Alert>
          ) : null}

          <Box
            ref={scrollerRef}
            sx={{
              height: posts.length > 0 ? 280 : 'auto',
              minHeight: posts.length > 0 ? 280 : 80,
              maxHeight: posts.length > 0 ? 280 : 80,
              overflowY: posts.length > 0 ? 'auto' : 'hidden',
              borderRadius: 1,
              background: '#f8f9fa',
              p: posts.length > 0 ? 2 : 1.5,
              mb: 2,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : posts.length ? (
              <Stack spacing={2}>
                {posts.map((p) => {
                  const isMe = !!myUserId && p.userId === myUserId;
                  const authorEmail = (p.User as any)?.email as string | undefined;
                  const authorUsername = (p.User as any)?.username || usernameFromEmail(authorEmail);
                  const authorDisplayName = (p.User as any)?.displayName || authorUsername;
                  const initials = authorDisplayName?.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
                  const createdAt = p.createdAt ? new Date(p.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
                  
                  return (
                    <Box 
                      key={p.id} 
                      sx={{ 
                        display: 'flex', 
                        gap: 1.5,
                        flexDirection: isMe ? 'row-reverse' : 'row',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          fontSize: '0.75rem',
                          bgcolor: isMe ? '#2563eb' : '#6b7280',
                        }}
                      >
                        {initials}
                      </Avatar>
                      <Box sx={{ maxWidth: '75%', minWidth: 0 }}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            background: isMe ? '#2563eb' : '#ffffff',
                            color: isMe ? '#ffffff' : '#1f2937',
                            border: isMe ? 'none' : '1px solid rgba(0,0,0,0.08)',
                            boxShadow: isMe ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                              {isMe ? 'You' : authorDisplayName}
                            </Typography>
                            {createdAt && (
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                {createdAt}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.875rem' }}>
                            {p.content}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No messages yet. Start the conversation!
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
            <TextField
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Type a message…"
              fullWidth
              multiline
              maxRows={4}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                },
              }}
            />
            <Button 
              variant="contained" 
              onClick={send} 
              disabled={!message.trim() || !discussion?.id}
              sx={{
                minWidth: 80,
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Send
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};


